import { TagPill } from '@brush/ui';
import { Check, ChevronDown, Plus, Search, Shield } from 'lucide-react';
import { useState } from 'react';

/**
 * Applying tags to a tournament.
 *
 * Platform tags render with a shield and cannot be removed once applied (locked product rule).
 * Room-defined tags stay removable.
 *
 * `suggestions` arrives most-used first, and only the first two rows of it are shown: a room with
 * forty tags reaches for the same six, and a wall of chips is harder to read than a short list.
 * Everything else is a click away in the browser below, which is where a rarely-used tag is
 * actually found — by name, rather than by scanning.
 */

/**
 * Roughly two rows at the widths these dialogs use. Not exact — chips are as wide as their tag —
 * but the point is a couple of lines rather than a precise count, and a fixed number keeps the
 * form from changing height as tags come and go.
 */
const PREVIEW_COUNT = 14;

export function TagInput({
  value,
  onChange,
  suggestions = [],
  platformTags = [],
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Every tag the room can apply, most-used first — see `rankedTags`. */
  suggestions?: string[];
  platformTags?: string[];
}) {
  const [draft, setDraft] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const [search, setSearch] = useState('');

  const isPlatform = (tag: string) =>
    platformTags.some((p) => p.toLowerCase() === tag.toLowerCase());
  const isApplied = (tag: string) => value.some((t) => t.toLowerCase() === tag.toLowerCase());

  const add = (tag: string) => {
    const clean = tag.trim();
    if (!clean || isApplied(clean)) return;
    onChange([...value, clean]);
    setDraft('');
  };
  const remove = (tag: string) =>
    onChange(value.filter((t) => t.toLowerCase() !== tag.toLowerCase()));

  /**
   * In the browser, a chip is a checkbox. Platform tags are the exception: applying one is a
   * decision the room cannot walk back, so an applied platform tag has no off state rather than
   * one that silently does nothing.
   */
  const toggle = (tag: string) => {
    if (!isApplied(tag)) add(tag);
    else if (!isPlatform(tag)) remove(tag);
  };

  const unused = suggestions.filter((s) => !isApplied(s));
  const preview = unused.slice(0, PREVIEW_COUNT);
  const query = search.trim().toLowerCase();
  const matching = query
    ? suggestions.filter((s) => s.toLowerCase().includes(query))
    : suggestions;

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {value.length === 0 ? (
          <span className="text-xs text-ink-800/40">No tags yet</span>
        ) : (
          value.map((tag) =>
            isPlatform(tag) ? (
              <TagPill key={tag} label={tag} kind="platform" />
            ) : (
              <TagPill key={tag} label={tag} kind="room" onRemove={() => remove(tag)} />
            ),
          )
        )}
      </div>

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder="Add a tag"
          className="min-w-0 flex-1 rounded-lg border border-ink-800/15 px-3 py-1.5 text-sm outline-none focus:border-felt-500"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={!draft.trim()}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-felt-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-felt-900 disabled:opacity-50"
        >
          <Plus className="size-3" /> Add
        </button>
      </div>

      {suggestions.length > 0 ? (
        <div className="mt-2">
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wider text-ink-800/40">
              {browsing ? `All tags (${suggestions.length})` : 'Most used'}
            </span>
            <button
              type="button"
              onClick={() => {
                setBrowsing((open) => !open);
                setSearch('');
              }}
              aria-expanded={browsing}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-felt-700 hover:text-felt-900"
            >
              {browsing ? 'Show fewer' : `Browse all ${suggestions.length}`}
              <ChevronDown
                className={`size-3 transition-transform ${browsing ? 'rotate-180' : ''}`}
              />
            </button>
          </div>

          {browsing ? (
            <div className="rounded-lg border border-ink-800/15 bg-white p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-800/35" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tags"
                  aria-label="Search tags"
                  className="w-full rounded-md border border-ink-800/15 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-felt-500"
                />
              </div>

              <div className="mt-2 max-h-44 overflow-auto">
                <div className="flex flex-wrap gap-1.5">
                  {matching.map((tag) => {
                    const applied = isApplied(tag);
                    const locked = applied && isPlatform(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggle(tag)}
                        disabled={locked}
                        aria-pressed={applied}
                        title={
                          locked
                            ? 'Platform tags stay on a tournament once applied.'
                            : undefined
                        }
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition ${
                          applied
                            ? 'border-felt-500 bg-felt-50 text-felt-900'
                            : 'border-ink-800/15 text-ink-800/70 hover:border-felt-500 hover:text-felt-900'
                        } ${locked ? 'cursor-default opacity-70' : ''}`}
                      >
                        {applied ? <Check className="size-3" /> : null}
                        {isPlatform(tag) ? <Shield className="size-2.5" /> : null}
                        {tag}
                      </button>
                    );
                  })}
                </div>
                {matching.length === 0 ? (
                  <p className="px-1 py-3 text-xs text-ink-800/50">
                    No tag matches “{search.trim()}”. Type it in the box above to create it.
                  </p>
                ) : null}
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-ink-800/10 pt-2">
                <span className="text-[10px] text-ink-800/45">
                  {value.length} applied · click to add or remove
                </span>
                <button
                  type="button"
                  onClick={() => setBrowsing(false)}
                  className="text-[11px] font-medium text-felt-700 hover:text-felt-900"
                >
                  Done
                </button>
              </div>
            </div>
          ) : preview.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {preview.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => add(tag)}
                  className="inline-flex items-center gap-1 rounded-full border border-ink-800/15 px-2.5 py-1 text-xs text-ink-800/70 hover:border-felt-500 hover:text-felt-900"
                >
                  {isPlatform(tag) ? <Shield className="size-2.5" /> : null}
                  {tag}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-ink-800/40">Every tag is already applied.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
